import React, {useEffect, useState, memo, Suspense, useCallback} from "react"
import {useTemplatedComponent} from "@next/templating"
import {useModalsCloseObservable} from "@next/eventservice"
import {MANIFEST} from "@manifest/header"
import touchDevice from "../../utils/is-touch-device"
import connect from "./connect"
import {calculateAlignment} from "./component"
import SnailItem, {Data} from "../SnailItem"
import TabsScroll from "../Scroller"
import {
    PRIMARY_NAV_ITEM_HOVER_DELAY,
    DRAWER_GAP,
    PRIMARY_NAV_SCROLL_ANIMATION_DELAY,
    VISITED_PAGES,
    PRIMARY_NAV_ALIGNMENT_TYPES,
} from "../../config/constants"
import {screenIsSmallerThanLarge, screenIsBiggerOrEqualToLarge} from "../../utils/window"
import {calcScrollOffset} from "../../utils/calculateWindowOffsets"
import {PrimaryNavItem} from "../../models/primary-nav"
import {PublishToModalsClosed} from "../../events/modalsClosed"
import {setDepartment} from "../../utils/setDepartment"
import {isPrimaryNavbarLink} from "../../utils/primaryNav"

type DisplayItem = PrimaryNavItem & {
    classNames: string
}

export type SnailTrailProps = {
    activeDepartmentIndex: number
    alignment?: string
    dir: string
    items: DisplayItem[]
    rememberSelectedDepartment: boolean
    setActiveItem: (
        index: number,
        item: {title: string; path: string; viewType?: string},
        activeDepartmentIndex: number,
    ) => void
    isPrimaryNavbarLink: (viewType?: string | undefined) => boolean
    setIsInPrimaryNav: (value: boolean, item?: {title: string; path: string; viewType?: string}) => void
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    closeTertiaryNav: () => void
}

let timerId: NodeJS.Timeout | number = 0
const clearHoverTimeout = () => clearTimeout(timerId as NodeJS.Timeout)

export const SnailTrail = memo(
    ({
        activeDepartmentIndex,
        alignment,
        dir,
        items,
        rememberSelectedDepartment,
        setActiveItem,
        setIsInPrimaryNav,
        templating,
        closeTertiaryNav,
    }: SnailTrailProps) => {
        const [isProgrammaticScrolling, setIsProgrammaticScrolling] = useState(false)
        const [loaded, setLoaded] = useState(false)
        const isTouchDevice = touchDevice()
        const timeOutDelay = isTouchDevice ? 0 : PRIMARY_NAV_ITEM_HOVER_DELAY

        const snailTrailRef = React.useRef<HTMLUListElement>(null)
        let currentlyActiveSnailItemRef: React.RefObject<HTMLLIElement>

        useEffect(() => {
            setLoaded(true)
        }, [])

        const close = useCallback(() => {
            setActiveItem(-1, {title: "", path: ""}, activeDepartmentIndex)
        }, [activeDepartmentIndex, setActiveItem])

        const onScroll = useCallback(() => {
            if (!isProgrammaticScrolling) {
                close()
            }
        }, [isProgrammaticScrolling, close])

        const memoizedCalcScrollOffset = React.useCallback(calcScrollOffset, [])

        const delayExecution = useCallback((functionToExecute: () => void, delay: number): void => {
            clearHoverTimeout()
            timerId = setTimeout(() => {
                functionToExecute()
                clearHoverTimeout()
            }, delay)
        }, [])

        const doScroll = useCallback(
            (event: React.MouseEvent<EventTarget>, clickedElement: HTMLElement): void => {
                setIsProgrammaticScrolling(true)
                const scrollOffset = memoizedCalcScrollOffset(event, clickedElement, DRAWER_GAP)
                const snailTrailElement = snailTrailRef.current as HTMLElement
                snailTrailElement.scrollLeft += scrollOffset

                delayExecution(() => setIsProgrammaticScrolling(false), PRIMARY_NAV_SCROLL_ANIMATION_DELAY)
            },
            [memoizedCalcScrollOffset, delayExecution],
        )

        const setActiveItemAndScroll = useCallback((data: Data, event: React.MouseEvent<EventTarget>) => {
            event.persist()
            return () => {
                const {index, item} = data
                const clickedElement = currentlyActiveSnailItemRef.current as HTMLElement
                setActiveItem(index, item, activeDepartmentIndex)
                doScroll(event, clickedElement)
            }
        }, [])

        const handleClick = useCallback(
            (event: React.MouseEvent<EventTarget>, ref: React.RefObject<HTMLLIElement>, data: Data): void => {
                currentlyActiveSnailItemRef = ref
                if (screenIsSmallerThanLarge() || isTouchDevice) {
                    event.preventDefault()
                    if (data.index === activeDepartmentIndex) {
                        close()
                    } else {
                        PublishToModalsClosed()
                        const {item} = data
                        setIsInPrimaryNav(true, item) // redux updates the isInPrimaryNav prop used in SecondaryNavContent to determine what meganav dropdown to show (mobile or desktop)
                        setActiveItemAndScroll(data, event)()
                        closeTertiaryNav()
                    }
                } else if (data.index === activeDepartmentIndex) {
                    localStorage.removeItem(VISITED_PAGES)
                    setDepartment(data.item.path, data.item.title.toLowerCase(), rememberSelectedDepartment)
                } else {
                    currentlyActiveSnailItemRef = ref
                    const {index, item} = data
                    setIsInPrimaryNav(true, item)
                    event.preventDefault()
                    setActiveItem(index, item, activeDepartmentIndex)
                    PublishToModalsClosed()
                }
            },
            [],
        )

        useModalsCloseObservable(close)

        const handleMouseEnter = useCallback(
            (event: React.MouseEvent<EventTarget>, ref: React.RefObject<HTMLLIElement>, data: Data): void => {
                currentlyActiveSnailItemRef = ref

                if (screenIsBiggerOrEqualToLarge()) {
                    const functionToExecuteLater = setActiveItemAndScroll(data, event)
                    delayExecution(functionToExecuteLater, timeOutDelay)
                    const {item} = data
                    setIsInPrimaryNav(true, item)
                    PublishToModalsClosed()
                }
            },
            [],
        )

        const handleKeyboardEnter = useCallback(
            (event: React.KeyboardEvent<EventTarget>, ref: React.RefObject<HTMLLIElement>, data: Data): void => {
                if (event.key !== "Enter") {
                    return
                }
                const {viewType} = data?.item

                if (screenIsBiggerOrEqualToLarge() && !isPrimaryNavbarLink(viewType)) {
                    if (data.index !== activeDepartmentIndex) {
                        currentlyActiveSnailItemRef = ref

                        const {index, item} = data
                        setIsInPrimaryNav(true, item)
                        event.preventDefault()
                        setActiveItem(index, item, activeDepartmentIndex)
                        PublishToModalsClosed()
                    }
                }
            },
            [],
        )

        const handleMouseLeave = useCallback(
            (_event: React.MouseEvent<EventTarget>) => {
                clearHoverTimeout()
                setIsInPrimaryNav(false)
            },
            [setIsInPrimaryNav],
        )

        // Note: Styling applied using style obj. This is because using emotion with the possible combination of justify-content: var(--platmod_meganav_alignment, space-between) does not return valid CSS when built.
        const alignmentCSS = `var(--platmod_meganav_alignment, ${calculateAlignment(
            alignment as PRIMARY_NAV_ALIGNMENT_TYPES,
        )})`

        const Container = useTemplatedComponent(templating, MANIFEST)

        return (
            <TabsScroll dir={dir} snailTrailRef={snailTrailRef} onMouseLeave={handleMouseLeave} alignment={alignment}>
                <Suspense>
                    <Container
                        alignment={alignment}
                        loaded={loaded}
                        onScroll={onScroll}
                        reference={snailTrailRef}
                        alignmentCSS={alignmentCSS}
                    >
                        {items.map(
                            (
                                {
                                    target,
                                    title,
                                    path,
                                    linkColour,
                                    fontWeight,
                                    classNames,
                                    mapping,
                                    pin,
                                    hidden,
                                    viewType,
                                    pictures,
                                },
                                index,
                            ) => {
                                const props = {
                                    index,
                                    isActive: activeDepartmentIndex === index,
                                    target,
                                    title,
                                    path,
                                    viewType,
                                    handleClick,
                                    handleMouseEnter,
                                    handleKeyboardEnter,
                                    linkColour,
                                    fontWeight,
                                    classNames,
                                    mapping,
                                    pin,
                                    hidden: hidden ?? false,
                                    pictures,
                                }
                                return <SnailItem key={mapping ?? title} {...props} />
                            },
                        )}
                    </Container>
                </Suspense>
            </TabsScroll>
        )
    },
)
SnailTrail.displayName = "SnailTrail"

export default connect(SnailTrail) as any
